import React from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { apiLogout } from "@/services/api";
import { Leaf, LayoutDashboard, LogOut, BookOpen, Menu, X } from "lucide-react";
import { useState } from "react";

export default function Layout({ children }) {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    try { await apiLogout(token); } catch {}
    logout();
    navigate("/login");
  };

  const navItems = [
    { label: "Vezérlőpult", icon: LayoutDashboard, path: "/dashboard" },
    { label: "Kurzusaim", icon: BookOpen, path: "/dashboard" },
    ...(user?.role === "superheadadmin" || user?.role === "admin" ? [
      { label: "Admin", icon: Leaf, path: "/admin/dashboard" }
    ] : []),
  ];

  const NavContent = () => (
    <>
      <div className="flex items-center gap-3 px-4 py-6 mb-4" style={{ borderBottom: "1.5px solid #242220" }}>
        <img
          src="/athena_logo.svg"
          alt="Logo"
          className="w-10 h-10"
          style={{ borderRadius: "14px" }}
        />
        <div>
          <p className="font-bold text-sm leading-tight" style={{ color: "#242220" }}>NaturTrak Athena</p>
          <p className="text-xs" style={{ color: "#242220", opacity: 0.6 }}>Tanulási Platform</p>
        </div>
      </div>

      <nav className="flex-1 px-3 space-y-1">
        {navItems.map((item) => {
          const active = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-2xl font-bold text-sm transition-all"
              style={{
                background: active ? "#FBD556" : "transparent",
                border: active ? "1.5px solid #242220" : "1.5px solid transparent",
                boxShadow: active ? "3px 3px 0 #242220" : "none",
                color: "#242220",
                textDecoration: "none",
              }}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              <item.icon size={18} strokeWidth={2.5} />
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="px-3 pb-6 mt-auto">
        <div
          className="p-3 mb-3"
          style={{ background: "#A1E1B9", borderRadius: "16px", border: "1.5px solid #242220" }}
        >
          <p className="text-xs font-bold" style={{ color: "#242220" }}>Bejelentkezve</p>
          <p className="text-sm font-bold truncate" style={{ color: "#242220" }}>
            {user?.name || user?.email_address || "Felhasználó"}
          </p>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 py-2.5 font-bold text-sm transition-all active:translate-x-[1px] active:translate-y-[1px]"
          style={{
            background: "#FDF5EB",
            borderRadius: "20px",
            border: "1.5px solid #242220",
            boxShadow: "3px 3px 0 #242220",
            color: "#242220",
            cursor: "pointer",
          }}
          data-testid="logout-btn"
        >
          <LogOut size={16} strokeWidth={2.5} />
          Kijelentkezés
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen" style={{ background: "#FDF5EB", fontFamily: "Quicksand, sans-serif" }}>
      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex flex-col w-64 fixed top-0 left-0 h-full"
        style={{ background: "#fff", borderRight: "1.5px solid #242220" }}
      >
        <NavContent />
      </aside>

      {/* Mobile header */}
      <div
        className="lg:hidden fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 py-3"
        style={{ background: "#fff", borderBottom: "1.5px solid #242220" }}
      >
        <div className="flex items-center gap-2">
          <img
            src="/athena_logo.svg"
            alt="Logo"
            className="w-8 h-8"
            style={{ borderRadius: "10px" }}
          />
          <span className="font-bold text-sm" style={{ color: "#242220" }}>NaturTrak Athena LMS</span>
        </div>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          style={{ color: "#242220" }}
          data-testid="mobile-menu-btn"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-30"
          onClick={() => setMobileOpen(false)}
          style={{ background: "rgba(36,34,32,0.4)" }}
        >
          <aside
            className="flex flex-col w-72 h-full"
            style={{ background: "#fff", borderRight: "1.5px solid #242220" }}
            onClick={(e) => e.stopPropagation()}
          >
            <NavContent />
          </aside>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-4 lg:p-8">{children}</div>
      </main>
    </div>
  );
}
